import { useMediaQuery } from 'usehooks-ts';
import React, { PropsWithChildren, useState } from 'react';

import { PopupProfile } from '../components/tabs';
import { useTimers } from '../hooks/use-game-timer';
import { Overlay } from '../features/in-game/overlay';
import DrawerMobileContainer from '../features/in-game/overlay/drawer/mobile/container';

export interface OverlayState {
  gameName: string;
  gameLogo: string;
  activeTab: string;
  timerAssId?: string;
  drawerIsOpen: boolean;
  drawerMobileIsOpen: boolean;
  profileDialogIsOpen: boolean;
  isCameraPermissionGranted: boolean;
  isVideoPermissionGranted: boolean;
}

const DefaultOverlayState: OverlayState = {
  gameName: '',
  gameLogo: '',
  timerAssId: '',
  activeTab: 'info',
  drawerIsOpen: false,
  drawerMobileIsOpen: false,
  profileDialogIsOpen: false,
  isVideoPermissionGranted: false,
  isCameraPermissionGranted: false
};

const OverlayContext = React.createContext<
  [
    initialState: OverlayState,
    setState: React.Dispatch<React.SetStateAction<OverlayState>>
  ]
>([DefaultOverlayState, () => {}]);

const OverlayProvider = (props: PropsWithChildren) => {
  const [state, setState] = useState<OverlayState>(DefaultOverlayState);
  const isSmallDevice = useMediaQuery('(max-width:767px)');

  const { timers } = useTimers();
  const runningTimers = React.useMemo(
    () => timers.filter((t) => t.status === 'running' || t.status === 'paused'),
    [timers]
  );

  const toggleProfileDialog = () =>
    setState((prev) => ({
      ...prev,
      profileDialogIsOpen: !prev.profileDialogIsOpen
    }));

  // display the first running timer on first render
  React.useEffect(() => {
    if (runningTimers.length === 0) return;

    const timerId = runningTimers.at(0)?.id ?? '';
    setState((prev) => ({
      ...prev,
      timerAssId: timerId
    }));
  }, []);

  return (
    <OverlayContext.Provider value={[state, setState]}>
      {isSmallDevice ? (
        <DrawerMobileContainer>{props.children}</DrawerMobileContainer>
      ) : (
        props.children
      )}

      {state.profileDialogIsOpen && (
        <PopupProfile onToggleProfileWidget={toggleProfileDialog} />
      )}

      <Overlay />
    </OverlayContext.Provider>
  );
};

export { OverlayContext, OverlayProvider };
